/**
 * designerUtils.ts — Pure utility functions for designer nodes
 *
 * Extracted from the shared ~90% pattern across CharacterDesignerNode,
 * LocationDesignerNode, and ObjectDesignerNode.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import type React from 'react'
import type { AgentColorConfig } from '../../../../constants/agentColors'
import {
  type AudioAssetMetadataDict,
  type AudioAssetMetadataDictYMap,
  type BasicAssetMetadata,
  type BasicAssetMetadataYMap,
  type BasicImage,
  type BasicImageYMap,
  type CharacterAsset,
  type CharacterAssetYMap,
  type GeneratedAssetMetadata,
  type GeneratedAssetMetadataYMap,
  type GeneratedAudioYMap,
  type GeneratedImage,
  type GeneratedImageWithHistory,
  type GeneratedImageWithHistoryYMap,
  type GeneratedImageYMap,
  type GeneratedVideo,
  type GeneratedVideoYMap,
  type LocationAsset,
  type LocationAssetYMap,
  type NodeStatus,
  type ObjectAsset,
  type ObjectAssetYMap,
  type SoundAudioHistoryEntryDict,
  type SoundAudioHistoryEntryDictYMap,
  type SoundGenerationMetadataDict,
  type SoundGenerationMetadataDictYMap,
  type VoiceProfileDictYMap,
  YMapRecord,
  type _SoundAudioResultDict,
  type _SoundAudioResultDictYMap,
} from '../../../../generated/nodeDataTypes'
import { NODE_BG_SWATCH_COLORS } from './nodeBgColors'
import type { HistoryEntry } from './types'
import type { VoiceVariant } from './VoiceTabContent'

export type DesignerImageOutputYMap =
  | CharacterAssetYMap
  | LocationAssetYMap
  | ObjectAssetYMap

export type DesignerImageOutput =
  | CharacterAsset
  | LocationAsset
  | ObjectAsset
  | DesignerImageOutputYMap

export type DesignerMainImage =
  | GeneratedImageWithHistory
  | GeneratedImageWithHistoryYMap

export function getPickedIndex(
  value: { picked_index?: number } | undefined,
): number {
  return value?.picked_index ?? 0
}

export function getPickedVideoIndex(
  value:
    | {
        picked_index?: number
        picked_variant_id?: string | null
        history?: Array<{ result?: { id?: string } }>
      }
    | undefined,
): number {
  const stableId = value?.picked_variant_id
  if (stableId && value?.history) {
    const stableIndex = value.history.findIndex((entry) => entry.result?.id === stableId)
    if (stableIndex >= 0) return stableIndex
  }
  return getPickedIndex(value)
}

export function generatedImagePath(
  entry: GeneratedImage | GeneratedImageYMap | undefined,
): string | undefined {
  return entry?.result?.local_path
}

export function generatedAudioPath(
  entry: GeneratedAudioYMap | undefined,
): string | undefined {
  return entry?.result?.local_path
}

export function generatedVideoPath(
  entry: GeneratedVideo | GeneratedVideoYMap | undefined,
): string | undefined {
  return entry?.result?.local_path
}

export function soundAudioPath(
  entry: SoundAudioHistoryEntryDictYMap | undefined,
): string | undefined {
  return entry?.result?.local_path
}

function basicAssetMetadataToJson(
  metadata: BasicAssetMetadata | BasicAssetMetadataYMap | undefined,
): BasicAssetMetadata | undefined {
  if (!metadata) return undefined
  return {
    creation_time: metadata.creation_time,
    source: metadata.source,
    model: metadata.model,
    provider: metadata.provider,
    mode: metadata.mode,
    duration_seconds: metadata.duration_seconds,
    regenerated: metadata.regenerated,
    regeneration_time: metadata.regeneration_time,
    aspect_ratio: metadata.aspect_ratio,
  }
}

function basicImageToJson(image: BasicImage | BasicImageYMap | undefined): BasicImage | undefined {
  if (!image) return undefined
  return {
    id: image.id,
    local_path: image.local_path,
    metadata: basicAssetMetadataToJson(image.metadata),
  }
}

function generatedAssetMetadataToJson(
  metadata: GeneratedAssetMetadata | GeneratedAssetMetadataYMap | undefined,
): GeneratedAssetMetadata | undefined {
  if (!metadata) return undefined
  return {
    model: metadata.model,
    provider: metadata.provider,
    mode: metadata.mode,
    loop: metadata.loop,
    segments: metadata.segments,
    segment_start_times: metadata.segment_start_times,
    character_name: metadata.character_name,
    voice_id: metadata.voice_id,
    voice_name: metadata.voice_name,
    eleven_lab_voice_id: metadata.eleven_lab_voice_id,
    eleven_lab_voice_name: metadata.eleven_lab_voice_name,
    voice_type: metadata.voice_type,
    duration_seconds: metadata.duration_seconds,
    regenerated: metadata.regenerated,
    regeneration_time: metadata.regeneration_time,
    aspect_ratio: metadata.aspect_ratio,
    type: metadata.type,
  }
}

function audioAssetMetadataToJson(
  metadata: AudioAssetMetadataDictYMap | undefined,
): AudioAssetMetadataDict | undefined {
  if (!metadata) return undefined
  return {
    creation_time: metadata.creation_time,
    model: metadata.model,
    source: metadata.source,
  }
}

function soundAudioResultToJson(
  result: _SoundAudioResultDictYMap | undefined,
): _SoundAudioResultDict | undefined {
  if (!result) return undefined
  return {
    local_path: result.local_path,
    metadata: audioAssetMetadataToJson(result.metadata),
  }
}

function soundGenerationMetadataToJson(
  metadata: SoundGenerationMetadataDictYMap | undefined,
): SoundGenerationMetadataDict | undefined {
  if (!metadata) return undefined
  return {
    model: metadata.model,
    provider: metadata.provider,
    mode: metadata.mode,
    loop: metadata.loop,
    segments: metadata.segments,
    segment_start_times: metadata.segment_start_times,
    character_name: metadata.character_name,
    voice_id: metadata.voice_id,
    voice_type: metadata.voice_type,
    duration_seconds: metadata.duration_seconds,
  }
}

export function generatedImageToJson(entry: GeneratedImage | GeneratedImageYMap): GeneratedImage {
  return {
    user_prompt: entry.user_prompt,
    result: basicImageToJson(entry.result),
    refs: entry.refs?.map((ref) => basicImageToJson(ref) ?? {}),
    metadata: generatedAssetMetadataToJson(entry.metadata),
  }
}

export function soundAudioHistoryToJson(
  history: SoundAudioHistoryEntryDictYMap[] | undefined,
): SoundAudioHistoryEntryDict[] {
  return (history ?? []).map((entry) => ({
    user_prompt: entry.user_prompt,
    result: soundAudioResultToJson(entry.result),
    metadata: soundGenerationMetadataToJson(entry.metadata),
  }))
}

export function generatedImageHistoryToJson(
  history: Array<GeneratedImage | GeneratedImageYMap> | undefined,
): HistoryEntry[] {
  return (history ?? []).map((entry) => {
    const json = generatedImageToJson(entry)
    return {
      user_prompt: json.user_prompt,
      result: {
        id: json.result?.id,
        local_path: json.result?.local_path,
        metadata: {
          creation_time: json.result?.metadata?.creation_time,
          style: json.result?.metadata?.source,
          model: json.result?.metadata?.model,
          aspect_ratio: json.result?.metadata?.aspect_ratio,
        },
      },
      metadata: {
        style: json.metadata?.mode,
        model: json.metadata?.model,
      },
    }
  })
}

export function getDesignerMainImage(
  outputObj: DesignerImageOutput | undefined,
): DesignerMainImage | undefined {
  const images = outputObj?.images
  if (!images) return undefined
  if (images instanceof YMapRecord) {
    return images.get('main')
  }
  return images.main
}

/**
 * Resolve the currently-picked image path from a designer output object.
 *
 * Reads `outputObj.images.main.history[picked_index].result.local_path`.
 */
export function resolveDesignerImagePath(
  outputObj: DesignerImageOutput | undefined,
): string | undefined {
  const main = getDesignerMainImage(outputObj)
  const history = main?.history ?? []
  return generatedImagePath(history[getPickedIndex(main)])
}

export function voiceProfileCacheKey(
  profile: VoiceProfileDictYMap | undefined,
): string | undefined {
  if (!profile) return undefined
  return [
    profile.provider,
    profile.voice_id,
    profile.voice_name,
    profile.audio_sample,
    profile.speech_text,
  ].filter(Boolean).join(':')
}

export function voiceHistoryToVariants(
  history: GeneratedAudioYMap[],
  projectId: string | undefined,
  cacheKey: string | undefined,
  getAssetUrl: (projectId: string, assetPath: string, width?: number, cacheKey?: string) => string | null,
): VoiceVariant[] {
  return history
    .map((entry, index): VoiceVariant => {
      const audioSample = generatedAudioPath(entry)
      return {
        index,
        provider: 'elevenlabs',
        voiceId: entry.metadata?.eleven_lab_voice_id ?? entry.metadata?.voice_id,
        voiceName: entry.metadata?.eleven_lab_voice_name ?? entry.metadata?.voice_name ?? entry.metadata?.voice_id,
        audioSample,
        audioUrl: audioSample && projectId
          ? getAssetUrl(projectId, audioSample, 0, `${audioSample}:${cacheKey ?? ''}`)
          : null,
        speechText: entry.user_prompt,
        createdAt: entry.result?.metadata?.creation_time,
      }
    })
    .filter((variant) => Boolean(variant.voiceName || variant.voiceId || variant.audioSample))
}

/**
 * Border color state machine for designer nodes.
 *
 * Priority: error > selected > running > hovered > default.
 *
 * When the node carries a user-assigned background color (`bgColorKey`, a
 * NODE_BG_SWATCH_COLORS key other than `default`), the *idle* and *hovered*
 * borders are derived from that custom color so the border matches the chosen
 * fill instead of the agent identity palette. Selected / running keep the agent
 * identity color and error keeps the semantic destructive color — mirroring
 * GroupNode, which likewise derives only idle/hover from the custom color and
 * keeps a semantic selection color. Unknown / `default` / undefined keys fall
 * back to the agent palette (same silent-fallback contract as
 * `resolveNodeBgColor`).
 *
 * Returns a React.CSSProperties object with borderColor and optional boxShadow.
 */
/**
 * Selection reads white, not the node's agent colour.
 *
 * Each agent has its own accent, so a selected node used to be outlined in
 * whatever colour that node already wore — against a dark canvas full of
 * coloured nodes, "selected" was hard to pick out, and worse on a marquee
 * where the point is to see at a glance what you caught. White belongs to
 * nothing else on the canvas, so it reads as state rather than identity.
 */
export const SELECTED_BORDER_COLOR = '#FFFFFF'
export const SELECTED_BORDER_GLOW = '0 0 0 1px rgba(255,255,255,0.55), 0 0 18px 4px rgba(255,255,255,0.22)'

export function computeBorderStyle(
  selected: boolean,
  status: NodeStatus,
  isHovered: boolean,
  agentColor: AgentColorConfig,
  bgColorKey?: string
): React.CSSProperties {
  if (status === 'error') {
    return { borderColor: 'var(--destructive)' }
  }
  if (selected) {
    return { borderColor: SELECTED_BORDER_COLOR, boxShadow: SELECTED_BORDER_GLOW }
  }
  if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') {
    return { borderColor: agentColor.active }
  }
  // Custom node color (if set) drives the idle + hover border. Swatch values are
  // 6-digit hex, so appending '80' yields a valid ~50%-alpha #RRGGBBAA (matches
  // GroupNode). Falsy lookup (unknown/default key) falls back to agentColor.
  const customBorder =
    bgColorKey && bgColorKey !== 'default' ? NODE_BG_SWATCH_COLORS[bgColorKey] : undefined
  if (isHovered) {
    return { borderColor: customBorder ?? agentColor.focus }
  }
  return { borderColor: customBorder ? customBorder + '80' : agentColor.hover }
}

/**
 * Border color for the SELECTED cell in a node's variant picker.
 *
 * This is agent IDENTITY, and deliberately no longer the same value as the
 * node card's selected border, which is now white — see
 * SELECTED_BORDER_COLOR. The two used to be required to match, on the
 * reasoning that the picker is only visible while the node is selected so
 * both meant "selected". They answer different questions: the card's border
 * says which node you have selected, the picker's says which variant is
 * chosen. Only the first needed to stand out against a crowded canvas.
 *
 * What must NOT come back is the picker reading an unrelated legacy palette
 * (`ACCENT_COLORS` in BaseNode.tsx) via a hardcoded per-call-site string,
 * which silently drifted from each node's agent identity: the character node
 * (yellow) rendered a purple picker border and the object node (amber) a cyan
 * one, while location matched only by coincidence.
 */
export function resolveVariantSelectionColor(agentColor: AgentColorConfig): string {
  return agentColor.active
}
